import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Dropdown, Button } from "antd";
import { GlobalOutlined, DownOutlined } from "@ant-design/icons";
import { SUPPORTED_LANGUAGES, SupportedLanguageCode } from "../i18n";
import { LANG_TO_PATH, switchLangPath } from "../i18n/routing";
import type { MenuProps } from "antd";

interface LanguageSwitcherProps {
  className?: string;
  size?: "small" | "middle" | "large";
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className,
  size = "middle",
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLangCode = (i18n.language || "ja").split("-")[0] as SupportedLanguageCode;
  const currentLang =
    SUPPORTED_LANGUAGES.find((lang) => lang.code === currentLangCode) ||
    SUPPORTED_LANGUAGES[0];

  const handleLanguageChange = (code: SupportedLanguageCode) => {
    const targetLangPath = LANG_TO_PATH[code] || "jp";
    const nextPath = switchLangPath(location.pathname, targetLangPath);
    i18n.changeLanguage(code);
    navigate(`${nextPath}${location.search}${location.hash}`);
  };

  const menuItems: MenuProps["items"] = SUPPORTED_LANGUAGES.map((lang) => ({
    key: lang.code,
    label: (
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span>{lang.flag}</span>
        <span>{lang.name}</span>
      </span>
    ),
    onClick: () => handleLanguageChange(lang.code),
  }));

  return (
    <Dropdown menu={{ items: menuItems, selectedKeys: [currentLang.code] }} trigger={["click", "hover"]}>
      <Button
        size={size}
        className={className}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          borderRadius: "6px",
          fontWeight: 500,
        }}
      >
        <GlobalOutlined style={{ fontSize: "16px" }} />
        <span>{currentLang.flag} {currentLang.name}</span>
        <DownOutlined style={{ fontSize: "10px", opacity: 0.7 }} />
      </Button>
    </Dropdown>
  );
};

export default LanguageSwitcher;
